// There are several different Excel files:

// - An Excel file for updating with ALL scenarios (pass no scenario ids)
// - An Excel file for updating with only some scenario ids
// - The above both for "public" and for "private" scenarios

// - An empty Excel for importing new scenarios

import { useContext, useState } from "react";
import { useIntl } from "react-intl";

import { AuthMode, downloadScenarioMetadata } from "@nens/ldo/api-v1";
import { LdoAuthContext } from "@nens/ldo/auth-v1";

import { BulkAction } from "./bulk-action-type";

type ExcelDownloadBulkActionParams =
  | {
      type: "all";
      mode: AuthMode;
    }
  | {
      type: "scenarios";
      mode: AuthMode;
      scenarioIds: string[];
    }
  | {
      type: "new";
    };

export function useExcelDownloadBulkAction(params: ExcelDownloadBulkActionParams): BulkAction {
  const intl = useIntl();
  const [running, setRunning] = useState<boolean>(false);
  const { apiFetchFunctionBareResponse } = useContext(LdoAuthContext);

  const excelType = params.type;

  const importNewTitle = intl.formatMessage({
    id: "bulkaction.download_new_excel_file",
    defaultMessage: "Download empty Excel file for new imports",
  });
  const importSomeTitle = intl.formatMessage({
    id: "bulkaction.download_metadata_selected_scenarios",
    defaultMessage: "Download metadata for selected scenarios",
  });
  const importAllTitle = intl.formatMessage({
    id: "bulkaction.download_metadata_all_scenarios",
    defaultMessage: "Download metadata for all scenarios",
  });

  if (excelType === "new") {
    return {
      title: importNewTitle,
      hidden: true,
      disabled: true,
      running: false,
      run: () => undefined,
    };
  }

  const run = async () => {
    setRunning(true);
    const mode = params.mode;

    await downloadScenarioMetadata(
      apiFetchFunctionBareResponse,
      excelType === "scenarios"
        ? { scenarioIds: params.scenarioIds.map((id) => Number.parseInt(id)) }
        : { mode }
    );

    setRunning(false);
  };

  return {
    title: excelType === "scenarios" ? importSomeTitle : importAllTitle,
    hidden: false,
    disabled: running || (excelType === "scenarios" && params.scenarioIds.length === 0),
    running,
    run,
  };
}
